import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { InventoryManagementApi } from '@buspro/web/apis';
import { AppService } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-provisioned-resources',
  standalone: true,
  imports: [NzTableModule, NzTagModule, NzTimelineModule, DatePipe],
  template: `
    <div class="p-8">
      <h1 class="text-3xl font-bold mb-6">Provisioned Resources</h1>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Resources Summary -->
        <div class="theme-page-card-bg rounded-lg shadow-md p-6">
          <h2 class="text-xl font-semibold mb-4">Allocated Resources</h2>
          <nz-table [nzData]="resources()" [nzLoading]="query.isLoading()" [nzSize]="'small'">
            <thead>
              <tr>
                <th>Item Type</th>
                <th>Name</th>
                <th>Status</th>
                <th>Bus</th>
              </tr>
            </thead>
            <tbody>
              @for (item of resources(); track item.id) {
                <tr>
                  <td>
                    <nz-tag [nzColor]="item.itemType === 'MDVR_DEVICE' ? 'blue' : 'green'">
                      {{ item.itemType }}
                    </nz-tag>
                  </td>
                  <td>{{ item.name }}</td>
                  <td>
                    <nz-tag [nzColor]="getStatusColor(item.status)">
                      {{ item.status }}
                    </nz-tag>
                  </td>
                  <td>{{ item.allocatedToBusName || 'Tenant-level' }}</td>
                </tr>
              }
            </tbody>
          </nz-table>
        </div>

        <!-- Allocation Timeline -->
        <div class="theme-page-card-bg rounded-lg shadow-md p-6">
          <h2 class="text-xl font-semibold mb-4">Allocation History</h2>
          <nz-timeline>
            @for (item of resources(); track item.id) {
              <nz-timeline-item [nzColor]="getTimelineColor(item.status)">
                <p class="font-semibold">{{ item.name }} - {{ item.itemType }}</p>
                <p class="text-sm text-gray-600">
                  Allocated to: {{ item.allocatedToBusName || 'Tenant' }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ item.allocatedAt | date: 'short' }}
                </p>
              </nz-timeline-item>
            }
          </nz-timeline>
        </div>
      </div>
    </div>
  `,
})
export class TenantProvisionedResourcesPageComponent {
  private api = inject(InventoryManagementApi);
  private appService = inject(AppService);

  tenantId = this.appService.currentTenant$()?.id || '';

  query = injectQuery(() => ({
    queryKey: ['tenant-provisioned-resources', this.tenantId],
    queryFn: () =>
      lastValueFrom(
        this.api.inventoryControllerGetTenantProvisionedResources({
          tenantId: this.tenantId,
        }),
      ),
    enabled: !!this.tenantId,
  }));

  resources = () =>
    (this.query.data() as unknown as Array<{
      id: string;
      itemType: string;
      name: string;
      status: string;
      allocatedToBusName?: string | null;
      allocatedAt?: Date | null;
    }>) || [];

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      IN_STOCK: 'default',
      ALLOCATED: 'processing',
      INSTALLED: 'success',
      FAULTY: 'error',
    };
    return colors[status] || 'default';
  }

  getTimelineColor(status: string): string {
    const colors: Record<string, string> = {
      IN_STOCK: 'gray',
      ALLOCATED: 'blue',
      INSTALLED: 'green',
      FAULTY: 'red',
    };
    return colors[status] || 'gray';
  }
}
